'use client';

import type { PortableTextBlock } from 'next-sanity';
import type { ReactNode } from 'react';
import { useEffect, useRef, useState } from 'react';
import { cn } from '../../../lib/utils';
import { convertPortableTextToPlainText } from '../../../sanity/utils/helpers';
import { Container } from '../../container';
import { CollapsibleRoot, CollapsibleTrigger } from '../../ui/collapsible';

const SPLIT_REGEX = /\s+/;
const PREVIEW_WORD_COUNT = 200;
const PREVIEW_HEIGHT = 200; // px

function countWordsInPortableText(content: PortableTextBlock[]): number {
  const plainText = convertPortableTextToPlainText(content);
  return plainText.trim() ? plainText.trim().split(SPLIT_REGEX).length : 0;
}

export function ProductListingSecondaryDescription({
  content,
  containerClassName,
  buttonClassName,
  children,
}: {
  content: PortableTextBlock[] | null;
  containerClassName?: string;
  buttonClassName?: string;
  children: (content: PortableTextBlock[]) => ReactNode;
}) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [showButton, setShowButton] = useState(false);
  const [contentHeight, setContentHeight] = useState<number>(PREVIEW_HEIGHT);
  const contentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (contentRef.current) {
      // Check if content exceeds preview height
      const scrollHeight = contentRef.current.scrollHeight;
      setShowButton(scrollHeight > PREVIEW_HEIGHT);
      setContentHeight(scrollHeight);
    }
  }, []);

  if (!content) return null;

  const totalWords = countWordsInPortableText(content);
  const hasLongContent = totalWords > PREVIEW_WORD_COUNT;

  if (!hasLongContent) {
    return (
      <Container className={containerClassName}>{children(content)}</Container>
    );
  }

  return (
    <Container className={containerClassName}>
      <CollapsibleRoot open={isExpanded} onOpenChange={setIsExpanded}>
        <div className="relative">
          {/* Full content with animated height */}
          <div
            ref={contentRef}
            className="relative overflow-hidden transition-[max-height] duration-300 ease-out"
            style={{
              maxHeight: isExpanded
                ? `${contentHeight}px`
                : `${PREVIEW_HEIGHT}px`,
            }}
          >
            {children(content)}
          </div>

          {/* Gradient overlay when collapsed */}
          {!isExpanded && showButton && (
            <div className="pointer-events-none absolute right-0 bottom-0 left-0 h-20 bg-gradient-to-t from-white via-white/95 to-transparent" />
          )}
        </div>

        {/* Toggle button */}
        {showButton && (
          <CollapsibleTrigger
            className={cn('mt-4 transition-colors', buttonClassName)}
          >
            {isExpanded ? 'Read less' : 'Read more'}
          </CollapsibleTrigger>
        )}
      </CollapsibleRoot>
    </Container>
  );
}
